import React from 'react';
import { Users } from 'lucide-react';
import { useServiceLineNames } from '../../lib/financeCatalogs';
import PhotoUpload from '../../components/common/PhotoUpload';
import { digitsOnly, isValidMobile10, isValidAadhaar12, mobileHint, aadhaarHint } from '../../lib/inputRules';

interface CommunicationsProps {
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  /** Stable facility / client id — used as customer_<id> for Cloudinary. */
  customerId?: string | null;
}

export function Communications({ formData, setFormData, onSubmit, onBack, customerId }: CommunicationsProps) {
  const lineOptions = useServiceLineNames();
  const setMobile = (key: string, raw: string) =>
    setFormData({ ...formData, [key]: digitsOnly(raw, 10) });

  return (
    <div className="app-card overflow-hidden">
      <div className="app-card-inner">
        <div className="app-section-head">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center border border-emerald-800 bg-emerald-800 text-white">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="app-section-title">Step 3 — Parties & files</h2>
              <p className="app-section-desc">Customer, guarantor, references, and uploads (optional fields).</p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6">
          <div>
            <label className="app-label">Customer name</label>
            <input
              type="text"
              className="app-field"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
            />
          </div>
          <div>
            <label className="app-label">Mobile number</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="app-field"
              value={formData.mobileNumber}
              onChange={(e) => setMobile('mobileNumber', e.target.value)}
              placeholder="10-digit mobile"
            />
            <p className={`mt-1 text-[11px] ${isValidMobile10(formData.mobileNumber) ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {mobileHint(formData.mobileNumber)}
            </p>
          </div>
          <div>
            <label className="app-label">Alternate mobile</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="app-field"
              value={formData.alternateMobile}
              onChange={(e) => setMobile('alternateMobile', e.target.value)}
              placeholder="Optional 10-digit"
            />
            {formData.alternateMobile ? (
              <p className={`mt-1 text-[11px] ${isValidMobile10(formData.alternateMobile) ? 'text-emerald-700' : 'text-amber-700'}`}>
                {mobileHint(formData.alternateMobile)}
              </p>
            ) : null}
          </div>
          <div>
            <label className="app-label">Guarantor name</label>
            <input
              type="text"
              className="app-field"
              value={formData.guarantorName}
              onChange={(e) => setFormData({ ...formData, guarantorName: e.target.value })}
            />
          </div>
          <div>
            <label className="app-label">Guarantor mobile</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="app-field"
              value={formData.guarantorMobile}
              onChange={(e) => setMobile('guarantorMobile', e.target.value)}
              placeholder="Optional 10-digit"
            />
            {formData.guarantorMobile ? (
              <p className={`mt-1 text-[11px] ${isValidMobile10(formData.guarantorMobile) ? 'text-emerald-700' : 'text-amber-700'}`}>
                {mobileHint(formData.guarantorMobile)}
              </p>
            ) : null}
          </div>
          <div>
            <label className="app-label">Referer name</label>
            <input
              type="text"
              className="app-field"
              value={formData.refererName}
              onChange={(e) => setFormData({ ...formData, refererName: e.target.value })}
            />
          </div>
          <div>
            <label className="app-label">Referer number</label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="app-field"
              value={formData.refererNumber}
              onChange={(e) => setMobile('refererNumber', e.target.value)}
              placeholder="Optional 10-digit"
            />
            {formData.refererNumber ? (
              <p className={`mt-1 text-[11px] ${isValidMobile10(formData.refererNumber) ? 'text-emerald-700' : 'text-amber-700'}`}>
                {mobileHint(formData.refererNumber)}
              </p>
            ) : null}
          </div>
          <div>
            <label className="app-label">Customer Aadhar</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={12}
              className="app-field"
              value={formData.customerAadhar}
              onChange={(e) => setFormData({ ...formData, customerAadhar: digitsOnly(e.target.value, 12) })}
              placeholder="12-digit Aadhaar"
            />
            <p className={`mt-1 text-[11px] ${isValidAadhaar12(formData.customerAadhar) ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {aadhaarHint(formData.customerAadhar)}
            </p>
          </div>
          <div>
            <label className="app-label">Village / town</label>
            <input
              type="text"
              className="app-field"
              value={formData.villageTown}
              onChange={(e) => setFormData({ ...formData, villageTown: e.target.value })}
            />
          </div>
          <div>
            <label className="app-label">Line</label>
            <select
              className="app-field"
              value={formData.line}
              onChange={(e) => setFormData({ ...formData, line: e.target.value })}
              disabled={lineOptions.length === 0}
            >
              <option value="">
                {lineOptions.length ? 'Select a service line' : 'Add lines in Settings → Service lines'}
              </option>
              {lineOptions.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Customer address 1</label>
            <textarea
              className="app-field min-h-[5rem] resize-y"
              rows={2}
              value={formData.customerAddress1}
              onChange={(e) => setFormData({ ...formData, customerAddress1: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Customer address 2 (optional)</label>
            <textarea
              className="app-field min-h-[5rem] resize-y"
              rows={2}
              value={formData.customerAddress2}
              onChange={(e) => setFormData({ ...formData, customerAddress2: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Guarantor address</label>
            <textarea
              className="app-field min-h-[5rem] resize-y"
              rows={2}
              value={formData.guarantorAddress}
              onChange={(e) => setFormData({ ...formData, guarantorAddress: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Record details</label>
            <textarea
              className="app-field min-h-[6rem] resize-y"
              rows={3}
              value={formData.recordDetails}
              onChange={(e) => setFormData({ ...formData, recordDetails: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Upload documents</label>
            <input
              type="file"
              multiple
              className="app-field file:mr-4 file:border-0 file:bg-emerald-800 file:px-4 file:py-2 file:text-sm file:font-bold file:text-white hover:file:bg-emerald-900"
              onChange={(e) => setFormData({ ...formData, documents: e.target.files })}
            />
          </div>

          <div className="md:col-span-2">
            <label className="app-label">Applicant photo</label>
            <PhotoUpload
              value={formData.applicantPhoto}
              onChange={(v) => setFormData({ ...formData, applicantPhoto: v })}
              customerId={customerId}
              label="Shown at the top-right of the printed application form."
            />
          </div>

          <div className="md:col-span-2 mt-2 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5">
            <button type="button" onClick={onBack} className="app-btn-secondary">
              Back
            </button>
            <button type="submit" className="app-btn-primary">
              Save to workspace
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
